import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, RefreshCw, Search, ShieldAlert } from 'lucide-react';

import { PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  fetchRecoveryCases,
  selectRecoveryCases,
  selectRecoveryCasesError,
  selectRecoveryCasesFetched,
  selectRecoveryCasesLoading,
  selectRecoveryCasesNextToken,
  type RecoveryCaseDisplayStatus,
  type RecoveryCaseStatus,
} from '@/features/recovery';
import { formatUnixDateTimeCompact } from '@/lib';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

const STATUS_VARIANTS: Record<RecoveryCaseDisplayStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  EVIDENCE_VERIFIED: 'outline',
  PENDING_REVIEW: 'secondary',
  APPROVED: 'default',
  EXECUTING: 'secondary',
  OPERATOR_REVIEW_REQUIRED: 'destructive',
  RESERVATION_RELEASE_PENDING: 'secondary',
  COMPLETED: 'default',
  REJECTED: 'outline',
  CANCELLED: 'outline',
  FAILED: 'destructive',
  UNKNOWN: 'destructive',
};

type RecoveryListFilter = RecoveryCaseStatus | 'ALL';
type RecoverySearchField = 'ALL' | 'CASE_ID' | 'NEW_PHONE_LAST_FOUR' | 'STATUS' | 'SUPPORT_REFERENCE';

const RECOVERY_STATUS_FILTERS: RecoveryListFilter[] = [
  'ALL',
  'PENDING_REVIEW',
  'APPROVED',
  'EXECUTING',
  'OPERATOR_REVIEW_REQUIRED',
  'RESERVATION_RELEASE_PENDING',
  'EVIDENCE_VERIFIED',
  'COMPLETED',
  'REJECTED',
  'CANCELLED',
  'FAILED',
];

const STATUS_LABELS: Record<RecoveryCaseDisplayStatus | 'ALL', string> = {
  ALL: 'All cases',
  EVIDENCE_VERIFIED: 'Evidence verified',
  PENDING_REVIEW: 'Pending review',
  APPROVED: 'Approved',
  EXECUTING: 'Executing',
  OPERATOR_REVIEW_REQUIRED: 'Operator review',
  RESERVATION_RELEASE_PENDING: 'Cleanup pending',
  COMPLETED: 'Completed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
  UNKNOWN: 'Malformed',
};

const SEARCH_FIELD_LABELS: Record<RecoverySearchField, string> = {
  ALL: 'All available fields',
  CASE_ID: 'Case ID',
  NEW_PHONE_LAST_FOUR: 'New phone — last 4',
  STATUS: 'Case status',
  SUPPORT_REFERENCE: 'Support reference',
};

function lastFourDigits(value?: string): string | undefined {
  const digits = value?.replace(/\D/g, '');
  return digits && digits.length >= 4 ? digits.slice(-4) : undefined;
}

function matchesRecoverySearch(
  recoveryCase: {
    caseId: string;
    status: RecoveryCaseDisplayStatus;
    newPrimaryPhoneHint?: string;
    support?: { ticketID?: string };
  },
  searchField: RecoverySearchField,
  query: string,
): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  const phoneQuery = normalizedQuery.replace(/\D/g, '');
  const newPhoneLastFour = lastFourDigits(recoveryCase.newPrimaryPhoneHint);
  const matchesNewPhoneLastFour = phoneQuery.length === 4 && newPhoneLastFour === phoneQuery;
  const matchesCaseId = recoveryCase.caseId.toLowerCase().includes(normalizedQuery);
  const matchesStatus =
    recoveryCase.status.toLowerCase().includes(normalizedQuery) ||
    STATUS_LABELS[recoveryCase.status].toLowerCase().includes(normalizedQuery);
  const matchesSupportReference = recoveryCase.support?.ticketID?.toLowerCase().includes(normalizedQuery) ?? false;

  switch (searchField) {
    case 'CASE_ID':
      return matchesCaseId;
    case 'NEW_PHONE_LAST_FOUR':
      return matchesNewPhoneLastFour;
    case 'STATUS':
      return matchesStatus;
    case 'SUPPORT_REFERENCE':
      return matchesSupportReference;
    case 'ALL':
      return matchesCaseId || matchesNewPhoneLastFour || matchesStatus || matchesSupportReference;
  }
}

function proofSummary(proofs: {
  existingEmailVerifiedAt?: number;
  newPhoneVerifiedAt?: number;
  contextSubmittedAt?: number;
}) {
  const completed = [proofs.existingEmailVerifiedAt, proofs.newPhoneVerifiedAt, proofs.contextSubmittedAt].filter(
    Boolean,
  ).length;
  return `${completed}/3 complete`;
}

export default function RecoveryCasesPage() {
  const dispatch = useAppDispatch();
  const cases = useAppSelector(selectRecoveryCases);
  const isLoading = useAppSelector(selectRecoveryCasesLoading);
  const hasFetched = useAppSelector(selectRecoveryCasesFetched);
  const nextToken = useAppSelector(selectRecoveryCasesNextToken);
  const error = useAppSelector(selectRecoveryCasesError);
  const [activeStatus, setActiveStatus] = useState<RecoveryListFilter>('PENDING_REVIEW');
  const [searchField, setSearchField] = useState<RecoverySearchField>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const requestRef = useRef<{ abort: () => void } | null>(null);

  const filteredCases = useMemo(
    () => cases.filter((recoveryCase) => matchesRecoverySearch(recoveryCase, searchField, searchQuery)),
    [cases, searchField, searchQuery],
  );

  useEffect(() => {
    const request = dispatch(fetchRecoveryCases(activeStatus === 'ALL' ? {} : { status: activeStatus }));
    requestRef.current = request;
    return () => {
      request.abort();
      if (requestRef.current === request) requestRef.current = null;
    };
  }, [activeStatus, dispatch]);

  const refresh = () => {
    if (!isLoading) {
      const request = dispatch(fetchRecoveryCases(activeStatus === 'ALL' ? {} : { status: activeStatus }));
      requestRef.current = request;
    }
  };

  const loadMore = () => {
    if (isLoading || !nextToken) return;
    const request = dispatch(
      fetchRecoveryCases({
        ...(activeStatus === 'ALL' ? {} : { status: activeStatus }),
        nextToken,
      }),
    );
    requestRef.current = request;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Account recovery cases"
        description={
          activeStatus === 'ALL'
            ? 'Complete masked recovery-case inventory, including records that need integrity review.'
            : `${STATUS_LABELS[activeStatus]} homeowner recovery cases. The API verifies current authority for every request.`
        }
        right={
          <Button type="button" variant="outline" onClick={refresh} disabled={isLoading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <Tabs value={activeStatus} onValueChange={(value) => setActiveStatus(value as RecoveryListFilter)}>
        <TabsList className="w-full justify-start overflow-x-auto">
          {RECOVERY_STATUS_FILTERS.map((status) => (
            <TabsTrigger key={status} value={status} className="px-3">
              {STATUS_LABELS[status]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-col gap-3 sm:flex-row">
        <Select value={searchField} onValueChange={(value) => setSearchField(value as RecoverySearchField)}>
          <SelectTrigger className="w-full sm:w-56" aria-label="Search recovery cases by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(SEARCH_FIELD_LABELS) as RecoverySearchField[]).map((field) => (
              <SelectItem key={field} value={field}>
                {SEARCH_FIELD_LABELS[field]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={
              searchField === 'NEW_PHONE_LAST_FOUR'
                ? 'Enter the proposed phone’s last four digits'
                : 'Search loaded recovery cases…'
            }
            aria-label="Search loaded recovery cases"
            className="pl-9"
          />
        </div>
      </div>
      {searchQuery.trim() ? (
        <p className="text-muted-foreground text-xs">
          Search applies to loaded queue rows and combines with the selected status filter. New-phone matching uses the
          visible last four digits only.
        </p>
      ) : null}

      <ErrorNotice message={error} title="Recovery cases unavailable" />

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Case</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Requested / updated</TableHead>
              <TableHead>New phone</TableHead>
              <TableHead>Proofs</TableHead>
              <TableHead>Memberships</TableHead>
              <TableHead>Support</TableHead>
              <TableHead>Review state</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && !hasFetched ? (
              <TableRow>
                <TableCell colSpan={8} className="h-28">
                  <div className="flex justify-center">
                    <LoadingSpinner />
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredCases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-32 text-center">
                  <div className="text-muted-foreground flex flex-col items-center gap-2">
                    {error ? <ShieldAlert className="h-5 w-5" /> : <ClipboardCheck className="h-5 w-5" />}
                    <span>
                      {error
                        ? 'Recovery cases could not be loaded.'
                        : cases.length > 0 && searchQuery.trim()
                          ? 'No loaded recovery cases match this search.'
                          : activeStatus === 'ALL'
                            ? 'No recovery cases were found.'
                            : `No ${STATUS_LABELS[activeStatus].toLowerCase()} recovery cases were found.`}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredCases.map((recoveryCase) => (
                <TableRow key={recoveryCase.caseId}>
                  <TableCell>
                    <Link
                      to={`/admin/recovery-cases/${encodeURIComponent(recoveryCase.caseId)}`}
                      className="font-medium underline-offset-4 hover:underline"
                    >
                      {recoveryCase.caseId}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant={STATUS_VARIANTS[recoveryCase.status]}>{STATUS_LABELS[recoveryCase.status]}</Badge>
                      {recoveryCase.integrity?.status === 'MALFORMED' ? (
                        <Badge variant="destructive">Integrity review</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    <div>
                      {recoveryCase.requestedAt ? formatUnixDateTimeCompact(recoveryCase.requestedAt) : 'Not available'}
                    </div>
                    <div>
                      {recoveryCase.updatedAt ? formatUnixDateTimeCompact(recoveryCase.updatedAt) : 'Not available'}
                    </div>
                  </TableCell>
                  <TableCell>{recoveryCase.newPrimaryPhoneHint ?? 'Not available'}</TableCell>
                  <TableCell>{proofSummary(recoveryCase.proofs)}</TableCell>
                  <TableCell>
                    {recoveryCase.propertyMembershipCount ?? '—'} properties /{' '}
                    {recoveryCase.organizationMembershipCount ?? '—'} orgs
                  </TableCell>
                  <TableCell className="max-w-40 truncate" title={recoveryCase.support?.ticketID}>
                    {recoveryCase.support?.ticketID ?? '—'}
                  </TableCell>
                  <TableCell>
                    {recoveryCase.integrity?.status === 'MALFORMED' ? (
                      <Badge variant="destructive">Integrity review</Badge>
                    ) : recoveryCase.canRetryCompletion ? (
                      <Badge variant="secondary">Retry completion</Badge>
                    ) : recoveryCase.status === 'OPERATOR_REVIEW_REQUIRED' ? (
                      <Badge variant="destructive">Operator review</Badge>
                    ) : recoveryCase.contextReview ? (
                      recoveryCase.contextReview
                    ) : (
                      'Pending'
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {nextToken ? (
        <div className="flex justify-center">
          <Button type="button" variant="outline" onClick={loadMore} disabled={isLoading}>
            {isLoading ? 'Loading more…' : 'Load more cases'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
